/**
 * Salas com nome (spec 018) — consultórios e bloco cirúrgico da clínica.
 *
 * Campo: `clinics/{cid}/config/agenda.salas: Sala[]`. É a FONTE dos nomes;
 * o número `config/agenda.consultorios` (regra de 15/10, `consultorios.ts`)
 * continua sendo gravado pela MESMA escrita de Configurações, como espelho
 * da quantidade de consultórios — assim a agenda, o formulário e o backend
 * do portal, que só leem o número, seguem certos sem deploy nenhum.
 *
 * O id é gerado e nunca é o nome: renomear "Consultório 1" pra "Sala do
 * Dr. Rafael" não mexe em liberação nenhuma.
 *
 * Cada faixa liberada pode dizer em que sala o médico está
 * (`releases[].salaId`). A faixa antiga, sem sala, é ESTIMADA na hora de
 * mostrar (`resolveSalas`) — sala preferida do médico se livre, senão a
 * primeira livre —, mas a tela grava a sala em toda faixa nova: recalcular
 * depois faria a sala de um médico mudar sozinha quando um colega liberasse.
 */
import { type ReleasedRange, timeToMinutes } from './blockedDates';
import {
  type DayGrade, parseAgendaDays, type ProfessionalSchedule, resolveDayGrade, resolveProfessionalDay,
} from './agendaGrade';
import type { AgendaConfigDay } from '@/types/models';
import { INTERNAL_UNIT_ID } from './internalUnit';

export type SalaTipo = 'CONSULTORIO' | 'BLOCO';

export interface Sala {
  id: string;
  nome: string;
  tipo: SalaTipo;
  /**
   * Horário próprio da sala (17/10/2026), no formato de
   * `config/agenda.days` — dia da semana → atende?, início, fim, almoço.
   * Ausente = a sala segue o horário da clínica. Existe porque as "salas"
   * da CEMEBS são lugares diferentes (Sala Cemebs, Conceição, NOANE…), cada
   * um com o seu expediente.
   */
  days?: Record<number, AgendaConfigDay>;
  /**
   * Unidade onde a sala fica (spec 019, fase 1). Ausente = a Sede
   * (`INTERNAL_UNIT_ID`), que é onde toda sala cadastrada antes das
   * unidades estava.
   */
  unitId?: string;
}

/** A unidade de toda sala sem `unitId` e de toda faixa sem sala: a Sede,
 *  a unidade que a clínica nasce tendo (`utils/internalUnit.ts`). */
export const SEDE_UNIT_ID = INTERNAL_UNIT_ID;

/** Unidades na ordem das telas: a Sede primeiro, depois as outras pelo
 *  nome, e as externas por último. */
export function orderUnits<T extends { id: string; name: string; externa?: boolean }>(units: T[]): T[] {
  const rank = (u: T) => (u.id === SEDE_UNIT_ID ? 0 : u.externa ? 2 : 1);
  return [...units].sort((a, b) => rank(a) - rank(b) || a.name.localeCompare(b.name, 'pt-BR'));
}

/** Unidade da sala. */
export const salaUnitId = (s: Sala): string => s.unitId ?? SEDE_UNIT_ID;

/** Salas de uma unidade, na ordem da lista. */
export const salasOfUnit = (salas: Sala[], unitId: string): Sala[] =>
  salas.filter((s) => salaUnitId(s) === unitId);

/**
 * Unidade de uma faixa liberada: a da sala dela. Faixa sem sala (antiga, ou
 * clínica sem salas) — ou com sala que não existe mais — é da Sede.
 */
export function rangeUnitId(r: ReleasedRange, salas: Sala[]): string {
  const sala = r.salaId ? salas.find((s) => s.id === r.salaId) : undefined;
  return sala ? salaUnitId(sala) : SEDE_UNIT_ID;
}

/** A sala como vai pro Firestore: sem nenhum `undefined` (o SDK recusa) e
 *  sem `days` quando ela segue a clínica. */
export function salaToDoc(s: Sala): Record<string, unknown> {
  const out: Record<string, unknown> = { id: s.id, nome: s.nome, tipo: s.tipo };
  if (s.unitId && s.unitId !== SEDE_UNIT_ID) out.unitId = s.unitId;
  if (s.days && Object.keys(s.days).length) {
    const days: Record<string, unknown> = {};
    for (const [k, d] of Object.entries(s.days)) {
      days[k] = {
        enabled: d.enabled,
        start: d.start,
        end: d.end,
        breaks: (d.breaks ?? []).map((b) => ({ start: b.start, end: b.end, ...(b.label ? { label: b.label } : {}) })),
      };
    }
    out.days = days;
  }
  return out;
}

// ── O médico segue o horário da sala onde está (17/10/2026) ──────────

const hhmmOf = (m: number) => `${String(Math.floor(m / 60)).padStart(2, '0')}:${String(m % 60).padStart(2, '0')}`;

/**
 * Junta vários horários num dia só: as partes úteis (início→fim menos os
 * intervalos) de cada um, unidas; o dia começa na mais cedo, termina na
 * mais tarde, e os buracos viram intervalo. Nenhum dia aberto = fechado.
 * Manhã na Sala Cemebs (9–12) + tarde na Conceição (13–18) = 9–18 com
 * intervalo 12–13.
 */
export function unionDays(days: Array<AgendaConfigDay | null | undefined>): AgendaConfigDay {
  const ivs: Array<[number, number]> = [];
  const labels = new Map<string, string>();
  for (const d of days) {
    if (!d || d.enabled === false) continue;
    let parts: Array<[number, number]> = [[timeToMinutes(d.start), timeToMinutes(d.end)]];
    for (const b of d.breaks ?? []) {
      if (b.label) labels.set(`${b.start}-${b.end}`, b.label);
      const bs = timeToMinutes(b.start); const be = timeToMinutes(b.end);
      parts = parts.flatMap(([s, e]): Array<[number, number]> => (be <= s || bs >= e ? [[s, e]] : [
        ...(bs > s ? [[s, bs] as [number, number]] : []),
        ...(be < e ? [[be, e] as [number, number]] : []),
      ]));
    }
    ivs.push(...parts.filter(([s, e]) => e > s));
  }
  if (!ivs.length) return { enabled: false, start: '08:00', end: '18:00', breaks: [] };
  ivs.sort((a, b) => a[0] - b[0]);
  const merged: Array<[number, number]> = [];
  for (const [s, e] of ivs) {
    const last = merged[merged.length - 1];
    if (last && s <= last[1]) last[1] = Math.max(last[1], e); else merged.push([s, e]);
  }
  const breaks = merged.slice(1).map(([s], i) => {
    const start = hhmmOf(merged[i][1]); const end = hhmmOf(s);
    return { start, end, label: labels.get(`${start}-${end}`) ?? 'Intervalo' };
  });
  return { enabled: true, start: hhmmOf(merged[0][0]), end: hhmmOf(merged[merged.length - 1][1]), breaks };
}

/**
 * Expediente do MÉDICO no dia seguindo as salas onde ele está liberado
 * (pedido de 17/10/2026). Sem liberação dele numa sala com horário próprio,
 * é o `base` (exceção da data → semana dele → clínica) sem mudança. Com:
 * cada faixa dele no dia vale o horário da sala dela (sala sem horário
 * próprio ou faixa sem sala = o `base`), e o dia é a união disso
 * (`unionDays`) — dia inteiro na Conceição = horário da Conceição.
 *
 * Cópia no backend do portal (`functions/src/proxy/portal.ts`,
 * `salaDayFor`). Mexeu numa, confira a outra.
 */
export function applySalaHours(
  base: AgendaConfigDay | null,
  weekday: number,
  dayReleases: ReleasedRange[] | undefined,
  salas: Sala[],
  professionalId: string | null | undefined,
): AgendaConfigDay | null {
  if (!professionalId || !salas.length) return base;
  const mine = (dayReleases ?? []).filter((r) => r.professionalId === professionalId);
  const roomOf = (r: ReleasedRange) => (r.salaId ? salas.find((s) => s.id === r.salaId) : undefined);
  if (!mine.some((r) => roomOf(r)?.days)) return base;
  return unionDays(mine.map((r) => {
    const room = roomOf(r);
    return room?.days ? (room.days[weekday] ?? null) : base;
  }));
}

/** `resolveProfessionalDayGrade` + o horário da sala do dia (ver
 *  `applySalaHours`) — a grade da Agenda, do formulário e da Liberar agenda. */
export function doctorDayGrade(
  prof: ProfessionalSchedule | null | undefined,
  unitId: string,
  dateStr: string,
  weekday: number,
  clinicDays: Record<number, AgendaConfigDay>,
  dayReleases: ReleasedRange[] | undefined,
  salas: Sala[],
  professionalId: string | null | undefined,
): DayGrade | null {
  const day = applySalaHours(
    resolveProfessionalDay(prof, unitId, dateStr, weekday, clinicDays), weekday, dayReleases, salas, professionalId);
  return day ? resolveDayGrade({ [weekday]: day }, weekday) : null;
}

/** Expediente da sala na data: o horário próprio dela, ou o da clínica. */
export function salaGrade(
  sala: Sala | null | undefined,
  weekday: number,
  clinicDays: Record<number, AgendaConfigDay>,
): DayGrade | null {
  return resolveDayGrade(sala?.days ?? clinicDays, weekday);
}

/** `config/agenda.salas` → lista válida, sem id repetido, na ordem gravada. */
export function parseSalas(raw: unknown): Sala[] {
  if (!Array.isArray(raw)) return [];
  const seen = new Set<string>();
  const out: Sala[] = [];
  for (const e of raw) {
    if (!e || typeof e !== 'object') continue;
    const s = e as Partial<Sala>;
    const id = typeof s.id === 'string' ? s.id.trim() : '';
    const nome = typeof s.nome === 'string' ? s.nome.trim() : '';
    if (!id || !nome || seen.has(id)) continue;
    seen.add(id);
    const days = parseAgendaDays((e as { days?: unknown }).days);
    const unitId = typeof s.unitId === 'string' && s.unitId.trim() ? s.unitId.trim() : undefined;
    out.push({
      id,
      nome,
      tipo: s.tipo === 'BLOCO' ? 'BLOCO' : 'CONSULTORIO',
      // Semana sem nenhum dia válido = sem horário próprio (segue a clínica).
      ...(Object.keys(days).length ? { days } : {}),
      // Sem unidade = Sede (não grava o padrão, pra não sujar o doc).
      ...(unitId && unitId !== SEDE_UNIT_ID ? { unitId } : {}),
    });
  }
  return out;
}

export const consultoriosOf = (salas: Sala[]) => salas.filter((s) => s.tipo === 'CONSULTORIO');
export const blocoOf = (salas: Sala[]) => salas.find((s) => s.tipo === 'BLOCO') ?? null;

/** Id curto e aleatório (8 caracteres). */
export function newSalaId(): string {
  const raw = typeof crypto !== 'undefined' && 'randomUUID' in crypto
    ? crypto.randomUUID()
    : `${Math.random().toString(36).slice(2)}${Date.now().toString(36)}`;
  return raw.replace(/-/g, '').slice(0, 8);
}

/** Clínica que só tinha o NÚMERO de consultórios → lista com nomes padrão
 *  (só pra tela de Configurações; nada é gravado até salvar). */
export function salasFromCount(n: number | null): Sala[] {
  return Array.from({ length: n ?? 0 }, (_, i) => ({ id: newSalaId(), nome: `Consultório ${i + 1}`, tipo: 'CONSULTORIO' as const }));
}

/** Rótulo curto: "C1", "C2"… na ordem dos consultórios; "BC" pro bloco —
 *  ou "B1", "B2" quando há mais de um (dois "BC" na mesma tela não dizem
 *  qual é qual). */
export function salaShort(sala: Sala, salas: Sala[]): string {
  if (sala.tipo === 'BLOCO') {
    const blocos = salas.filter((s) => s.tipo === 'BLOCO');
    return blocos.length > 1 ? `B${blocos.findIndex((s) => s.id === sala.id) + 1}` : 'BC';
  }
  return `C${consultoriosOf(salas).findIndex((s) => s.id === sala.id) + 1}`;
}

// ── Ocupação ─────────────────────────────────────────────────────────

interface Iv { s: number; e: number }
const DAY_END = 24 * 60;
const ivOf = (r: ReleasedRange): Iv => ({
  s: r.full ? 0 : timeToMinutes(r.start),
  e: r.full || r.end === '23:59' ? DAY_END : timeToMinutes(r.end),
});
const overlaps = (a: Iv, b: Iv) => a.s < b.e && b.s < a.e;
const hhmm = (m: number) =>
  m >= DAY_END ? '23:59' : `${String(Math.floor(m / 60)).padStart(2, '0')}:${String(m % 60).padStart(2, '0')}`;

export interface ResolvedRange {
  range: ReleasedRange;
  /** Sala efetiva, ou `null` (faixa sem dono, sem sala livre, sem salas). */
  salaId: string | null;
  /** `true` = faixa antiga sem `salaId`: a sala foi estimada agora. */
  estimada: boolean;
}

/**
 * Sala efetiva de cada faixa de UM dia, na ordem de entrada.
 *
 * Gravada vale como está. Antiga (sem `salaId`) é estimada em ordem
 * determinística (início, depois médico): procedimento → bloco; consulta →
 * sala preferida do médico se nenhum COLEGA estiver nela no horário, senão
 * o primeiro consultório livre. Faixa sem dono ("todos", antiga) não tem
 * sala — não dá pra saber quem está lá.
 */
export function resolveSalas(
  ranges: ReleasedRange[],
  salas: Sala[],
  preferidaDe: (professionalId: string) => string | undefined,
): ResolvedRange[] {
  const valid = new Set(salas.map((s) => s.id));
  const out: ResolvedRange[] = ranges.map((range) => ({
    range,
    salaId: range.salaId && valid.has(range.salaId) ? range.salaId : null,
    estimada: false,
  }));
  const bloco = blocoOf(salas);
  const consultorios = consultoriosOf(salas);
  const pending = out
    .filter((x) => !x.salaId && x.range.professionalId)
    .sort((a, b) => ivOf(a.range).s - ivOf(b.range).s
      || (a.range.professionalId ?? '').localeCompare(b.range.professionalId ?? ''));
  for (const x of pending) {
    const prof = x.range.professionalId!;
    if (x.range.procedures) {
      if (bloco) { x.salaId = bloco.id; x.estimada = true; }
      continue;
    }
    const iv = ivOf(x.range);
    const pref = preferidaDe(prof);
    const order = [
      ...consultorios.filter((s) => s.id === pref),
      ...consultorios.filter((s) => s.id !== pref),
    ];
    const free = order.find((s) => !out.some((o) =>
      o.salaId === s.id && o.range.professionalId && o.range.professionalId !== prof && overlaps(ivOf(o.range), iv)));
    if (free) { x.salaId = free.id; x.estimada = true; }
  }
  return out;
}

/**
 * Sala pra uma faixa NOVA (o "Automático"): procedimento → bloco; senão a
 * preferida do médico se livre, senão o primeiro consultório livre. As
 * faixas do próprio médico no dia não contam (vão ser trocadas/somadas).
 * `undefined` = clínica sem salas ou tudo ocupado (a trava de contagem do
 * `persist` decide se recusa).
 */
export function pickSala(
  dayRanges: ReleasedRange[],
  salas: Sala[],
  preferidaDe: (professionalId: string) => string | undefined,
  professionalId: string,
  range: ReleasedRange,
): string | undefined {
  if (range.procedures) return blocoOf(salas)?.id;
  const others = resolveSalas(dayRanges.filter((r) => r.professionalId !== professionalId), salas, preferidaDe);
  const iv = ivOf(range);
  const pref = preferidaDe(professionalId);
  const consultorios = consultoriosOf(salas);
  const order = [...consultorios.filter((s) => s.id === pref), ...consultorios.filter((s) => s.id !== pref)];
  return order.find((s) => !others.some((o) =>
    o.salaId === s.id && o.range.professionalId && overlaps(ivOf(o.range), iv)))?.id;
}

export interface SalaConflict {
  salaId: string;
  start: string;
  end: string;
  professionalIds: string[];
}

/** Trechos em que dois médicos DIFERENTES estão gravados no mesmo
 *  consultório. Bloco não conta (pode ter mais de uma equipe). */
function salaClashes(ranges: ReleasedRange[] | undefined, salas: Sala[]): SalaConflict[] {
  const rooms = new Set(consultoriosOf(salas).map((s) => s.id));
  const list = (ranges ?? []).filter((r) => r.salaId && rooms.has(r.salaId) && r.professionalId);
  const out: SalaConflict[] = [];
  for (let i = 0; i < list.length; i++) {
    for (let j = i + 1; j < list.length; j++) {
      const a = list[i]; const b = list[j];
      if (a.salaId !== b.salaId || a.professionalId === b.professionalId) continue;
      const ia = ivOf(a); const ib = ivOf(b);
      if (!overlaps(ia, ib)) continue;
      const s = Math.max(ia.s, ib.s); const e = Math.min(ia.e, ib.e);
      out.push({ salaId: a.salaId!, start: hhmm(s), end: hhmm(e), professionalIds: [a.professionalId!, b.professionalId!] });
    }
  }
  return out;
}

/** Minutos de choque de sala no dia — pra recusar só o que PIORA (o dia
 *  que já nasceu com choque ainda pode ser encurtado/limpo). */
export function salaClashMinutes(ranges: ReleasedRange[] | undefined, salas: Sala[]): number {
  return salaClashes(ranges, salas).reduce((acc, c) => acc + (timeToMinutes(c.end) - timeToMinutes(c.start)), 0);
}

export function salaConflict(ranges: ReleasedRange[] | undefined, salas: Sala[]): SalaConflict | null {
  return salaClashes(ranges, salas)[0] ?? null;
}

/**
 * Tira de um médico o trecho [start, end) em QUALQUER sala — "pôr o Rafael
 * no C1 de manhã" tira a manhã dele do C2, mas deixa a tarde onde está.
 * As outras faixas (de colegas, sem dono) passam intactas.
 */
export function cutReleased(
  ranges: ReleasedRange[],
  professionalId: string,
  start: string,
  end: string,
  full = false,
): ReleasedRange[] {
  const cut = ivOf({ start, end, full });
  const out: ReleasedRange[] = [];
  for (const r of ranges) {
    if (r.professionalId !== professionalId) { out.push(r); continue; }
    const iv = ivOf(r);
    if (!overlaps(iv, cut)) { out.push(r); continue; }
    const pieces: Iv[] = [];
    if (cut.s > iv.s) pieces.push({ s: iv.s, e: cut.s });
    if (cut.e < iv.e) pieces.push({ s: cut.e, e: iv.e });
    for (const p of pieces) out.push({ ...r, full: false, start: hhmm(p.s), end: hhmm(p.e) });
  }
  return out;
}
